/**
 * 👥 UNIFIED USER DIRECTORY - Smart user listing with multi-role support
 * Replaces scattered user management pages with unified view
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Paper,
  Pagination,
  Tooltip,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  Visibility,
  Edit,
  Message,
  Block,
  CheckCircle,
  Warning,
  Schedule,
  Star,
  TrendingUp,
  Close,
  Delete,
} from '@mui/icons-material';

import { UnifiedUser, VerificationStatus } from '@/types';
import { UserRoleBadges } from '@/components/users/UserRoleBadges';
import { adminApiService, type AdminUser } from '@/services/adminApiService';
import { EnhancedUserCard } from '@/components/users/EnhancedUserCard';
import { MultiRoleUserProfile } from '@/components/users/MultiRoleUserProfile';
import { EnhancedUserFilters, type FilterOptions } from '@/components/filters/EnhancedUserFilters';
import { verificationService } from '@/services/verificationService';
import { NotificationPreferencesDialog } from '@/components/users/NotificationPreferencesDialog';
import toast from 'react-hot-toast';

// Using FilterOptions from EnhancedUserFilters instead of local interface

export const UnifiedUserDirectory: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    registeredToday: 0,
    verified: 0,
    pendingVerification: 0,
    multiRoleUsers: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [multiRoleProfileOpen, setMultiRoleProfileOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | 'suspend' | 'delete' | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  // User verification rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [rejectingUser, setRejectingUser] = useState<{ userId: string; role: string } | null>(null);

  const [filters, setFilters] = useState<FilterOptions>({
    search: '',
    roleFilter: 'all',
    statusFilter: 'all',
    verificationFilter: 'all',
    dateRange: { start: null, end: null },
    location: { country: '', city: '' },
    completionRange: { min: 0, max: 100 },
    lastActivityDays: null,
    hasMultipleRoles: null,
    sortBy: 'registeredAt',
    sortOrder: 'desc'
  });

  // Load users from new backend API
  useEffect(() => {
    loadUsers();
  }, []);

  // Load verification data and enrich user data
  const loadVerificationData = async () => {
    try {
      const response = await verificationService.getAdminVerificationRequests();
      
      if (response.success && response.data?.data) {
        return response.data.data;
      }
    } catch (error) {
      console.error('❌ Error loading verification data:', error);
    }
    return [];
  };

  // Enrich user data with verification status
  const enrichUsersWithVerificationData = (users: AdminUser[], verifications: any[]) => {
    return users.map(user => {
      const userVerification = verifications.find(v => v.userId === user.id);
      return {
        ...user,
        verificationStatus: userVerification?.status || 'not_submitted',
        verificationId: userVerification?.verificationId,
        documentType: userVerification?.documentType,
        submittedAt: userVerification?.uploadedAt,
        hasVerification: !!userVerification
      };
    });
  };

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {

      // Load both users and verification data in parallel
      // Fetch ALL users (using a high limit) and apply filters client-side
      const [usersResponse, verificationData] = await Promise.all([
        adminApiService.getUsers({
          limit: 10000 // Fetch all users, don't limit to rowsPerPage
        }),
        loadVerificationData()
      ]);


      if (usersResponse.success && usersResponse.data) {

        // Enrich users with verification data
        const enrichedUsers = enrichUsersWithVerificationData(usersResponse.data.users || [], verificationData);

        setUsers(enrichedUsers as any);
        setStatistics({
          ...(usersResponse.data.statistics || {
            total: 0,
            registeredToday: 0,
            verified: 0,
            pendingVerification: 0,
            multiRoleUsers: 0
          }),
          // Add verification stats
          pendingVerification: verificationData.filter(v => v.status === 'pending_review').length,
          verified: verificationData.filter(v => v.status === 'verified').length
        });
        setFilteredUsers(enrichedUsers as any);

        // Clear error if we successfully loaded users
        setError(null);
      } else {
        // If no users but API responded, show empty state (not an error)
        setUsers([]);
        setFilteredUsers([]);
        setStatistics({
          total: 0,
          registeredToday: 0,
          verified: 0,
          pendingVerification: verificationData.filter(v => v.status === 'pending_review').length,
          multiRoleUsers: 0
        });
      }
    } catch (err) {
      console.error('❌ Error loading users:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
      setError(errorMessage);

      // Load verification data for fallback mock users
      loadMockUsersWithVerification();
    } finally {
      setIsLoading(false);
    }
  };

  // Reload users when search changes (debounced)
  // Other filters are applied client-side in applyEnhancedFiltering
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, filters.search ? 300 : 0); // Debounce search, but load immediately on mount

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  const loadMockUsersWithVerification = async () => {
    
    try {
      // Load verification data to show users who have submitted verifications
      const verificationData = await loadVerificationData();
      
      if (verificationData.length > 0) {
        
        // Create user records from verification submissions
        const usersFromVerifications = verificationData.map(verification => ({
          id: verification.userId,
          name: `${verification.userInfo.firstName} ${verification.userInfo.lastName}`,
          email: verification.userInfo.email || '',
          phone: verification.userInfo.phone || '',
          role: 'customer',
          status: 'active',
          registeredAt: verification.uploadedAt,
          isActive: true,
          // Verification data
          verificationStatus: verification.status,
          verificationId: verification.verificationId,
          documentType: verification.documentType,
          submittedAt: verification.uploadedAt,
          hasVerification: true
        }));
        
        setUsers(usersFromVerifications);
        setFilteredUsers(usersFromVerifications);
        setStatistics({
          total: usersFromVerifications.length,
          registeredToday: usersFromVerifications.filter(u => {
            const today = new Date().toDateString();
            return new Date(u.registeredAt).toDateString() === today;
          }).length,
          verified: verificationData.filter(v => v.status === 'verified').length,
          pendingVerification: verificationData.filter(v => v.status === 'pending_review').length,
          multiRoleUsers: 0
        });
      } else {
        loadMockUsers();
      }
    } catch (error) {
      console.error('❌ Error loading verification data for users:', error);
      loadMockUsers();
    }
  };

  const loadMockUsers = () => {
    
    // Show empty state instead of fake data
    setUsers([]);
    setFilteredUsers([]);
    setStatistics({
      total: 0,
      registeredToday: 0,
      verified: 0,
      pendingVerification: 0,
      multiRoleUsers: 0
    });
  };

  // Enhanced client-side filtering for all filter options
  const applyEnhancedFiltering = () => {
    if (users.length === 0) {
      setFilteredUsers([]);
      return;
    }

    let filtered = [...users];

    // Text search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.phone?.includes(filters.search) ||
        user.id.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (filters.roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (filters.roleFilter === 'multi-role') {
          return (user.roles?.length || 1) > 1;
        }
        return user.userType === filters.roleFilter || user.roles?.includes(filters.roleFilter);
      });
    }

    // Status filter
    if (filters.statusFilter !== 'all') {
      filtered = filtered.filter(user => {
        // Map internal status to filter values
        if (filters.statusFilter === 'active') return user.isVerified;
        if (filters.statusFilter === 'pending') return !user.isVerified;
        return true; // For suspended/banned, we'd need more user data
      });
    }

    // Verification filter
    if (filters.verificationFilter !== 'all') {
      filtered = filtered.filter(user => {
        const verification = user.driverVerification;
        if (filters.verificationFilter === 'verified') return user.isVerified;
        if (filters.verificationFilter === 'pending') return verification?.status === 'pending_review';
        if (filters.verificationFilter === 'rejected') return verification?.status === 'rejected';
        if (filters.verificationFilter === 'incomplete') return !user.isVerified && !verification;
        return true;
      });
    }

    // Location filter
    if (filters.location.country || filters.location.city) {
      filtered = filtered.filter(user => {
        if (!user.location) return false;
        const userLocation = user.location.toLowerCase();
        
        // Check country match
        if (filters.location.country && !userLocation.includes(filters.location.country.toLowerCase())) {
          return false;
        }
        
        // Check city match (only if city is specified)
        if (filters.location.city && !userLocation.includes(filters.location.city.toLowerCase())) {
          return false;
        }
        
        return true;
      });
    }

    // Date range filter
    if (filters.dateRange.start || filters.dateRange.end) {
      filtered = filtered.filter(user => {
        if (!user.createdAt) return false;
        const userDate = new Date(user.createdAt);
        
        if (filters.dateRange.start && userDate < filters.dateRange.start) return false;
        if (filters.dateRange.end && userDate > filters.dateRange.end) return false;
        return true;
      });
    }

    // Completion range filter
    if (filters.completionRange.min > 0 || filters.completionRange.max < 100) {
      filtered = filtered.filter(user => {
        const completion = user.driverVerification?.completionPercentage || 0;
        return completion >= filters.completionRange.min && completion <= filters.completionRange.max;
      });
    }

    // Multiple roles filter
    if (filters.hasMultipleRoles !== null) {
      filtered = filtered.filter(user => {
        const hasMultiple = (user.roles?.length || 1) > 1;
        return filters.hasMultipleRoles ? hasMultiple : !hasMultiple;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (filters.sortBy) {
        case 'registeredAt':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'verificationStatus':
          aValue = a.driverVerification?.completionPercentage || 0;
          bValue = b.driverVerification?.completionPercentage || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return filters.sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return filters.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
    setPage(1); // Reset to first page when filters change
  };

  // Apply filtering when users or filters change
  useEffect(() => {
    applyEnhancedFiltering();
  }, [users, filters]);

  const handleViewProfile = (userId: string) => {
    const user = filteredUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setMultiRoleProfileOpen(true);
    }
  };

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageContent, setMessageContent] = useState({ subject: '', message: '' });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUserData, setEditUserData] = useState<Partial<AdminUser>>({});
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteEmailConfirm, setDeleteEmailConfirm] = useState('');
  const [deleteAcknowledge, setDeleteAcknowledge] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [deletionLog, setDeletionLog] = useState<any>(null);
  const [bulkDeletionSummaryOpen, setBulkDeletionSummaryOpen] = useState(false);
  const [bulkDeletionLog, setBulkDeletionLog] = useState<any>(null);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaValue, setQuotaValue] = useState<number>(5);
  const [notificationPrefsDialogOpen, setNotificationPrefsDialogOpen] = useState(false);
  const [notificationPrefsUserId, setNotificationPrefsUserId] = useState<string | null>(null);

  const handleApproveUser = async (userId: string) => {
    try {
      setIsLoading(true);
      const result = await adminApiService.bulkApproveUsers([userId], 'Single user approval');

      if (result.success) {
        toast.success('User approved successfully');
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to approve user');
      }
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectUser = async (userId: string) => {
    const reason = prompt('Please provide a rejection reason:');
    if (!reason) return;

    try {
      setIsLoading(true);
      const result = await adminApiService.bulkRejectUsers([userId], reason);

      if (result.success) {
        toast.success('User rejected');
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to reject user');
      }
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setIsLoading(false);
    }
  };

  // User verification approval from Multi-Role Profile
  const handleApproveVerification = async (userId: string, role: string) => {
    try {
      setIsLoading(true);

      // First, get the user's verification requests to find the verificationId
      // Search across ALL statuses since the user might not be in 'pending_review'
      const verificationResponse = await verificationService.getAdminVerificationRequests({ status: 'all' });

      if (!verificationResponse.success || !verificationResponse.data) {
        throw new Error('Failed to fetch verification requests');
      }

      // Find the verification record for this user
      const userVerification = verificationResponse.data.data.find(v => v.userId === userId);

      if (!userVerification) {
        console.error('❌ Available verification records:', verificationResponse.data.data);
        console.error('❌ Looking for userId:', userId);
        throw new Error('No verification found for this user. The user may not have submitted verification documents yet.');
      }


      // Now approve using the verificationId
      const result = await verificationService.approveVerification(userVerification.verificationId, `${role} approved from multi-role profile`);

      if (result.success) {
        toast.success(`${role} verification approved successfully!`);
        await loadUsers();
        setMultiRoleProfileOpen(false);
      } else {
        toast.error(result.error || 'Failed to approve verification');
      }
    } catch (error) {
      console.error('Error approving verification:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to approve verification');
    } finally {
      setIsLoading(false);
    }
  };

  // User verification rejection from Multi-Role Profile - opens modal
  const handleRejectVerification = (userId: string, role: string) => {
    setRejectingUser({ userId, role });
    setRejectDialogOpen(true);
  };

  // Confirm rejection with reason
  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please select a rejection reason');
      return;
    }

    if (!rejectingUser) return;

    try {
      setIsLoading(true);

      // First, get the user's verification requests to find the verificationId
      // Search across ALL statuses since the user might not be in 'pending_review'
      const verificationResponse = await verificationService.getAdminVerificationRequests({ status: 'all' });

      if (!verificationResponse.success || !verificationResponse.data) {
        throw new Error('Failed to fetch verification requests');
      }

      // Find the verification record for this user
      const userVerification = verificationResponse.data.data.find(v => v.userId === rejectingUser.userId);

      if (!userVerification) {
        console.error('❌ Available verification records:', verificationResponse.data.data);
        console.error('❌ Looking for userId:', rejectingUser.userId);
        throw new Error('No verification found for this user. The user may not have submitted verification documents yet.');
      }


      // Now reject using the verificationId with the proper API
      const result = await verificationService.rejectVerification(
        userVerification.verificationId,
        rejectionReason,
        rejectionNotes || undefined,
        undefined // rejectedFields - let backend use default (both)
      );

      if (result.success) {
        toast.success(`${rejectingUser.role} verification rejected`);
        await loadUsers();
        setRejectDialogOpen(false);
        setMultiRoleProfileOpen(false);
        setRejectionReason('');
        setRejectionNotes('');
        setRejectingUser(null);
      } else {
        toast.error(result.error || 'Failed to reject verification');
      }
    } catch (error) {
      console.error('Error rejecting verification:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reject verification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageUser = (userId: string) => {
    const user = filteredUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setMessageDialogOpen(true);
    }
  };

  const sendMessage = async () => {
    if (!selectedUser || !messageContent.message) {
      toast.error('Please provide a message');
      return;
    }

    try {
      setIsLoading(true);
      const result = await adminApiService.sendMessageToUser(
        selectedUser.id,
        messageContent.message,
        messageContent.subject
      );

      if (result.success) {
        toast.success('Message sent successfully');
        setMessageDialogOpen(false);
        setMessageContent({ subject: '', message: '' });
      } else {
        toast.error(result.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (userId: string) => {
    const user = filteredUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setEditUserData({
        name: user.name,
        email: user.email,
        phone: user.phone,
        userType: user.userType
      });
      setEditDialogOpen(true);
    }
  };

  const saveUserEdit = async () => {
    if (!selectedUser) return;

    try {
      setIsLoading(true);
      const result = await adminApiService.updateUser(selectedUser.id, editUserData);

      if (result.success) {
        toast.success('User updated successfully');
        setEditDialogOpen(false);
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuspendUser = (userId: string) => {
    const user = filteredUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setSuspendDialogOpen(true);
    }
  };

  const confirmSuspend = async () => {
    if (!selectedUser || !suspendReason) {
      toast.error('Please provide a suspension reason');
      return;
    }

    try {
      setIsLoading(true);
      const result = await adminApiService.suspendUser(selectedUser.id, suspendReason);

      if (result.success) {
        toast.success('User suspended successfully');
        setSuspendDialogOpen(false);
        setSuspendReason('');
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to suspend user');
      }
    } catch (error) {
      toast.error('Failed to suspend user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = (userId: string) => {
    const user = filteredUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setDeleteDialogOpen(true);
      setDeleteReason('');
      setDeleteConfirmText('');
      setDeleteEmailConfirm('');
      setDeleteAcknowledge(false);
      setForceDelete(false);
      setDeletionLog(null);
    }
  };

  const confirmDelete = async () => {
    if (!selectedUser || !deleteReason) {
      toast.error('Please provide a deletion reason');
      return;
    }

    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type "DELETE" to confirm');
      return;
    }

    if (deleteEmailConfirm !== selectedUser.email) {
      toast.error('Email confirmation does not match');
      return;
    }

    if (!deleteAcknowledge) {
      toast.error('You must acknowledge that this action is permanent');
      return;
    }

    try {
      setIsLoading(true);
      const adminId = localStorage.getItem('userId') || 'admin-default';
      const result = await adminApiService.deleteUser(selectedUser.id, deleteReason, adminId, true, forceDelete);

      if (result.success) {
        toast.success('User permanently deleted');
        setDeletionLog(result.data?.deletionLog);
        // Don't close dialog immediately - show deletion log
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setIsLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteReason('');
    setDeleteConfirmText('');
    setDeleteEmailConfirm('');
    setDeleteAcknowledge(false);
    setForceDelete(false);
    setDeletionLog(null);
  };

  const handleEditQuota = (userId: string) => {
    const user = filteredUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setQuotaValue((user as any).referralQuota || 5);
      setQuotaDialogOpen(true);
    }
  };

  const handleManageNotifications = (userId: string) => {
    setNotificationPrefsUserId(userId);
    setNotificationPrefsDialogOpen(true);
  };

  const saveQuota = async () => {
    if (!selectedUser) return;

    try {
      setIsLoading(true);

      const response = await fetch(`/api/v1/admin/users/${selectedUser.id}/quota`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quota: quotaValue })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Referral quota updated successfully');
        setQuotaDialogOpen(false);
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to update quota');
      }
    } catch (error) {
      console.error('❌ Error updating quota:', error);
      toast.error('Failed to update quota');
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Operations Handlers
  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUserIds);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allUserIds = filteredUsers.map(user => user.id);
      setSelectedUserIds(new Set(allUserIds));
    } else {
      setSelectedUserIds(new Set());
    }
  };

  const handleBulkAction = (event: React.MouseEvent<HTMLElement>, action: 'approve' | 'reject' | 'suspend' | 'delete') => {
    // For delete action, handle it differently with prompts instead of dialog
    if (action === 'delete') {
      handleBulkDelete();
      return;
    }

    setBulkAction(action);
    setBulkConfirmOpen(true);
    setBulkActionMenuAnchor(null);
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.size === 0) return;

    const deleteReason = prompt('⚠️ PERMANENT DELETE - Provide detailed reason:');
    if (!deleteReason) return;

    const confirmText = prompt('Type "DELETE" in capitals to confirm permanent deletion:');
    if (confirmText !== 'DELETE') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }

    setIsLoading(true);
    try {
      const userIds = Array.from(selectedUserIds);
      const adminId = localStorage.getItem('userId') || 'admin-default';

      const result = await adminApiService.bulkDeleteUsers(userIds, deleteReason, adminId, true, false);

      if (result?.success) {
        toast.success(result.message || 'Users deleted successfully');

        // Show bulk deletion summary dialog
        if (result.data) {
          setBulkDeletionLog(result.data);
          setBulkDeletionSummaryOpen(true);
        }

        // Clear selection and reload
        setSelectedUserIds(new Set());
        await loadUsers();
      } else {
        toast.error(result?.error || 'Failed to delete users');
      }
    } catch (error) {
      console.error('❌ Error performing bulk delete:', error);
      toast.error('Failed to delete users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkAction || selectedUserIds.size === 0) return;

    setIsLoading(true);
    try {
      const userIds = Array.from(selectedUserIds);
      let result;

      switch (bulkAction) {
        case 'approve':
          result = await adminApiService.bulkApproveUsers(userIds, 'Bulk approval from User Hub');
          break;
        case 'reject':
          const reason = prompt('Please provide a rejection reason:');
          if (!reason) {
            setIsLoading(false);
            return;
          }
          result = await adminApiService.bulkRejectUsers(userIds, reason);
          break;
        case 'suspend':
          const suspendReason = prompt('Please provide a suspension reason:');
          if (!suspendReason) {
            setIsLoading(false);
            return;
          }
          result = await adminApiService.bulkSuspendUsers(userIds, suspendReason);
          break;
      }

      if (result?.success) {
        toast.success(result.message || `Bulk ${bulkAction} completed successfully`);
        // Clear selection and reload data
        setSelectedUserIds(new Set());
        await loadUsers();
      } else {
        toast.error(result?.error || `Failed to ${bulkAction} users`);
      }
    } catch (error) {
      console.error(`❌ Error performing bulk ${bulkAction}:`, error);
      toast.error(`Failed to ${bulkAction} users`);
    } finally {
      setIsLoading(false);
      setBulkConfirmOpen(false);
      setBulkAction(null);
    }
  };

  const getBulkActionText = () => {
    if (!bulkAction) return '';
    const count = selectedUserIds.size;
    const action = bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1);
    return `${action} ${count} selected user${count !== 1 ? 's' : ''}`;
  };

  const getRoleCount = (user: AdminUser) => {
    return user.roles?.length || 1;
  };

  const getStatusColor = (isVerified: boolean) => {
    return isVerified ? 'success' : 'warning';
  };

  const getVerificationStatus = (user: AdminUser) => {
    if (user.driverVerification) {
      return {
        status: user.driverVerification.status,
        percentage: user.driverVerification.completionPercentage
      };
    }
    return {
      status: user.isVerified ? 'approved' : 'pending',
      percentage: user.isVerified ? 100 : 50
    };
  };

  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Enhanced Filters */}
      <EnhancedUserFilters
        filters={filters}
        onFiltersChange={setFilters}
        onApplyFilters={applyEnhancedFiltering}
        userCount={filteredUsers.length}
        isLoading={isLoading}
      />

      {/* Bulk Operations Bar */}
      {selectedUserIds.size > 0 && (
        <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={(e) => handleBulkAction(e, 'approve')}
                  color="success"
                  variant="contained"
                >
                  Approve All
                </Button>
                <Button
                  size="small"
                  startIcon={<Warning />}
                  onClick={(e) => handleBulkAction(e, 'reject')}
                  color="error"
                  variant="outlined"
                >
                  Reject All
                </Button>
                <Button
                  size="small"
                  startIcon={<Block />}
                  onClick={(e) => handleBulkAction(e, 'suspend')}
                  color="warning"
                  variant="outlined"
                >
                  Suspend All
                </Button>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={(e) => handleBulkAction(e, 'delete')}
                  color="error"
                  variant="contained"
                  sx={{ bgcolor: 'error.dark', '&:hover': { bgcolor: 'error.main' } }}
                >
                  Delete All
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setSelectedUserIds(new Set())}
                >
                  <Close />
                </IconButton>
              </Stack>
            }
          >
            {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
          </Alert>
        )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>API Connection Issue:</strong> {error}. Showing cached data below.
          </Typography>
        </Alert>
      )}
      
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Showing <strong>{filteredUsers.length}</strong> users. 
          Total registered: <strong>{statistics.total}</strong> | 
          Verified: <strong>{statistics.verified}</strong> | 
          Registered today: <strong>{statistics.registeredToday}</strong>
          {statistics.multiRoleUsers > 0 && ` | Multi-role users: ${statistics.multiRoleUsers}`}
        </Typography>
      </Alert>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUserIds.size > 0 && selectedUserIds.size < filteredUsers.length}
                  checked={filteredUsers.length > 0 && selectedUserIds.size === filteredUsers.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  inputProps={{ 'aria-label': 'select all users' }}
                />
              </TableCell>
              <TableCell>User</TableCell>
              <TableCell>Type & Roles</TableCell>
              <TableCell>Verification</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell>Quota</TableCell>
              <TableCell>Used</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading users...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No users found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <EnhancedUserCard
                  key={user.id}
                  user={user}
                  selected={selectedUserIds.has(user.id)}
                  onSelect={(checked) => handleSelectUser(user.id, checked)}
                  onViewProfile={handleViewProfile}
                  onApprove={handleApproveUser}
                  onReject={handleRejectUser}
                  onMessage={handleMessageUser}
                  onEdit={handleEditUser}
                  onSuspend={handleSuspendUser}
                  onDelete={handleDeleteUser}
                  onEditQuota={handleEditQuota}
                  onManageNotifications={handleManageNotifications}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredUsers.length / rowsPerPage)}
          page={page}
          onChange={(e, newPage) => setPage(newPage)}
          color="primary"
        />
      </Box>

      {/* Multi-Role User Profile Dialog */}
      {selectedUser && (
        <MultiRoleUserProfile
          user={selectedUser}
          open={multiRoleProfileOpen}
          onClose={() => {
            setMultiRoleProfileOpen(false);
            setSelectedUser(null);
          }}
          onApprove={handleApproveVerification}
          onReject={handleRejectVerification}
          onMessage={(userId) => {
            // TODO: Implement user messaging
          }}
        />
      )}

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Bulk Action
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to <strong>{bulkAction}</strong> the following {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''}?
          </Typography>

          <Box sx={{ mt: 2, mb: 2 }}>
            {filteredUsers
              .filter(user => selectedUserIds.has(user.id))
              .slice(0, 5) // Show first 5 users
              .map(user => (
                <Typography key={user.id} variant="body2" color="text.secondary">
                  • {user.name || user.email} ({user.userType})
                </Typography>
              ))
            }
            {selectedUserIds.size > 5 && (
              <Typography variant="body2" color="text.secondary">
                ... and {selectedUserIds.size - 5} more users
              </Typography>
            )}
          </Box>

          <Alert severity="warning">
            This action cannot be undone. Please confirm you want to proceed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkConfirm}
            variant="contained"
            color={bulkAction === 'approve' ? 'success' : 'error'}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : `${bulkAction === 'approve' ? 'Approve' : bulkAction === 'reject' ? 'Reject' : 'Suspend'} All`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Verification Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectionReason('');
          setRejectionNotes('');
          setRejectingUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Reject {rejectingUser?.role} Verification
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please select a reason for rejecting this user's verification:
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Rejection Reason *</InputLabel>
              <Select
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                label="Rejection Reason *"
                required
              >
                <MenuItem value="Document image unclear">Document image unclear</MenuItem>
                <MenuItem value="Selfie doesn't match document">Selfie doesn't match document</MenuItem>
                <MenuItem value="Document appears fake/edited">Document appears fake/edited</MenuItem>
                <MenuItem value="Photo quality too low">Photo quality too low</MenuItem>
                <MenuItem value="Failed liveness check">Failed liveness check</MenuItem>
                <MenuItem value="Incomplete information">Incomplete information</MenuItem>
                <MenuItem value="Other">Other (please specify in notes)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Notes (Optional)"
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              placeholder="Provide additional details about the rejection..."
              helperText="These notes will be sent to the user"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
              setRejectionNotes('');
              setRejectingUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim() || isLoading}
          >
            {isLoading ? 'Rejecting...' : 'Reject Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={editUserData.name || ''}
              onChange={(e) => setEditUserData(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editUserData.email || ''}
              onChange={(e) => setEditUserData(prev => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              label="Phone"
              fullWidth
              value={editUserData.phone || ''}
              onChange={(e) => setEditUserData(prev => ({ ...prev, phone: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={editUserData.userType || ''}
                label="User Type"
                onChange={(e) => setEditUserData(prev => ({ ...prev, userType: e.target.value as any }))}
              >
                <MenuItem value="renter">Renter</MenuItem>
                <MenuItem value="host">Host</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveUserEdit} variant="contained" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message User Dialog */}
      <Dialog open={messageDialogOpen} onClose={() => setMessageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Message to {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Subject (Optional)"
              fullWidth
              value={messageContent.subject}
              onChange={(e) => setMessageContent(prev => ({ ...prev, subject: e.target.value }))}
            />
            <TextField
              label="Message"
              fullWidth
              required
              multiline
              rows={4}
              value={messageContent.message}
              onChange={(e) => setMessageContent(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Type your message here..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>Cancel</Button>
          <Button onClick={sendMessage} variant="contained" disabled={isLoading || !messageContent.message}>
            {isLoading ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend User Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend User: {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              Suspending a user will temporarily block their access to the platform.
            </Alert>
            <TextField
              label="Suspension Reason"
              fullWidth
              required
              multiline
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Please provide a reason for suspension..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmSuspend} variant="contained" color="error" disabled={isLoading || !suspendReason}>
            {isLoading ? 'Suspending...' : 'Suspend User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Quota Dialog */}
      <Dialog open={quotaDialogOpen} onClose={() => setQuotaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Referral Quota for {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Set the number of users this person can invite. Enter -1 for unlimited invites.
            </Alert>

            <TextField
              label="Invite Quota"
              type="number"
              fullWidth
              required
              value={quotaValue}
              onChange={(e) => setQuotaValue(parseInt(e.target.value) || 0)}
              helperText="Enter -1 for unlimited invites"
              inputProps={{ min: -1, step: 1 }}
            />

            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Current Usage: {(selectedUser as any)?.referralsUsed || 0} invites used
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuotaDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveQuota} variant="contained" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Quota'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Preferences Dialog */}
      {notificationPrefsUserId && (
        <NotificationPreferencesDialog
          open={notificationPrefsDialogOpen}
          onClose={() => {
            setNotificationPrefsDialogOpen(false);
            setNotificationPrefsUserId(null);
          }}
          userId={notificationPrefsUserId}
          userName={filteredUsers.find(u => u.id === notificationPrefsUserId)?.name}
        />
      )}

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
          ⚠️ DELETE USER - PERMANENT AND IRREVERSIBLE
        </DialogTitle>
        <DialogContent>
          {!deletionLog ? (
            <Box sx={{ pt: 2 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  WARNING: This action CANNOT be undone!
                </Typography>
                <Typography variant="body2">
                  This will permanently delete all user data including bookings, reviews, messages, and verification data.
                </Typography>
              </Alert>

              <Stack spacing={3}>
                <TextField
                  label="Deletion Reason"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Provide a detailed reason for deletion (required)..."
                />

                <TextField
                  label="Type user's email to confirm"
                  fullWidth
                  required
                  value={deleteEmailConfirm}
                  onChange={(e) => setDeleteEmailConfirm(e.target.value)}
                  placeholder={selectedUser?.email}
                  helperText={`Type: ${selectedUser?.email}`}
                />

                <TextField
                  label='Type "DELETE" in capital letters'
                  fullWidth
                  required
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  helperText="Must be exactly: DELETE"
                />

                <FormControl>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={deleteAcknowledge}
                      onChange={(e) => setDeleteAcknowledge(e.target.checked)}
                    />
                    <Typography variant="body2">
                      I understand this is permanent and cannot be undone
                    </Typography>
                  </Stack>
                </FormControl>

                <FormControl>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={forceDelete}
                      onChange={(e) => setForceDelete(e.target.checked)}
                      color="warning"
                    />
                    <Typography variant="body2" color="warning.main">
                      Force delete (even if user has active bookings)
                    </Typography>
                  </Stack>
                </FormControl>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                User deleted successfully!
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Deletion Log:
              </Typography>

              <Stack spacing={1}>
                {Object.entries(deletionLog).map(([key, value]) => (
                  <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {value as number} deleted
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>
            {deletionLog ? 'Close' : 'Cancel'}
          </Button>
          {!deletionLog && (
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              disabled={isLoading || !deleteReason || deleteConfirmText !== 'DELETE' || deleteEmailConfirm !== selectedUser?.email || !deleteAcknowledge}
            >
              {isLoading ? 'Deleting...' : 'Delete Forever'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Deletion Summary Dialog */}
      <Dialog
        open={bulkDeletionSummaryOpen}
        onClose={() => {
          setBulkDeletionSummaryOpen(false);
          setBulkDeletionLog(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.dark', fontWeight: 700 }}>
          ✅ Bulk Deletion Completed
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Users deleted successfully!
            </Alert>

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Deletion Summary:
            </Typography>

            {bulkDeletionLog && (
              <Stack spacing={1}>
                {Object.entries(bulkDeletionLog).map(([key, value]) => (
                  <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {value as number} deleted
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkDeletionSummaryOpen(false);
              setBulkDeletionLog(null);
            }}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};